/**
 * AddBillingProfileDialog — create a Billing Profile on the tenant.
 *
 * Vendor field mapping (POST /api/edi/note-to-claim/clients):
 *   - client_id   ← Billing Profile ID (4-digit numeric string the user picks)
 *   - description ← Billing Profile Name (free text)
 *
 * The vendor returns 400 "That Client ID already exists" if the ID is in
 * use — we surface that verbatim via readVendorError.
 */
import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, X, CheckCircle2, AlertTriangle } from 'lucide-react';
import { addClient } from '../../lib/api/ediApi';

function readVendorError(data, fallback) {
  const d = data?.detail;
  if (typeof d === 'string' && d) return d;
  if (Array.isArray(d) && d.length) {
    const msgs = d.map((e) => e?.msg || e?.message).filter(Boolean);
    if (msgs.length) return msgs.join('; ');
  }
  if (d && typeof d === 'object') {
    const issues = Array.isArray(d.issues) ? d.issues.filter(Boolean) : [];
    if (d.message && issues.length) return `${d.message}: ${issues.join('; ')}`;
    if (d.message) return d.message;
  }
  if (typeof data?.message === 'string' && data.message) return data.message;
  return fallback;
}

const AddBillingProfileDialog = ({ token, onClose, onAdded }) => {
  const [profileId, setProfileId] = useState('');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!/^\d{4}$/.test(profileId)) {
      setError('Billing Profile ID must be exactly 4 digits.');
      return;
    }
    if (!name.trim()) {
      setError('Billing Profile Name is required.');
      return;
    }
    setSaving(true);
    try {
      const { ok, status, data } = await addClient(token, {
        client_id: profileId,
        description: name.trim(),
      });
      if (!ok) {
        setError(readVendorError(data, `Create failed (HTTP ${status})`));
        return;
      }
      setSuccess(true);
      onAdded?.();
      setTimeout(() => { onClose?.(); }, 900);
    } catch (err) {
      setError(err.message || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4"
      data-testid="add-profile-dialog"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <p className="text-sm font-semibold text-slate-900">Add Billing Profile</p>
          <button
            onClick={() => { if (!saving) onClose?.(); }}
            disabled={saving}
            className="text-slate-400 hover:text-slate-700 transition-colors disabled:opacity-30"
            data-testid="add-profile-close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="space-y-2">
            <label className="text-[12px] text-slate-600 font-medium">
              Billing Profile ID
              <span className="ml-1 text-slate-400 font-normal">(4 digits)</span>
            </label>
            <Input
              value={profileId}
              onChange={(e) => setProfileId(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="1234"
              inputMode="numeric"
              maxLength={4}
              disabled={saving || success}
              className="font-mono text-sm"
              data-testid="add-profile-id-input"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <label className="text-[12px] text-slate-600 font-medium">Billing Profile Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Westside Family Clinic"
              maxLength={120}
              disabled={saving || success}
              className="text-sm"
              data-testid="add-profile-name-input"
            />
            <p className="text-[10px] text-slate-400">
              POST /api/edi/note-to-claim/clients
            </p>
          </div>

          {success && (
            <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[11px] text-emerald-700" data-testid="add-profile-success">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Billing profile created.
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-[11px] text-red-700" data-testid="add-profile-error">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!success && (
            <Button
              type="submit"
              disabled={saving || profileId.length !== 4 || !name.trim()}
              className="w-full bg-emerald-600 hover:bg-emerald-700 gap-1.5"
              data-testid="add-profile-submit"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Create profile
            </Button>
          )}
        </form>
      </div>
    </div>
  );
};

export default AddBillingProfileDialog;
